import type { BaseAttribute } from '../../types/attributes';
import { getAttrFromProductByName } from '../../types/attrNames';
import type { CmsOffer, CmsProduct } from '../../types/cmsProduct';
import { attributeCellText } from '../../utils/helpers/attrHelpers';
import { getChooseOneOffers } from '../../utils/helpers/offers';
import { parsePriceValue } from '../../utils/parsePriceValue';
import type {
  CompareCell,
  CompareConfig,
  ComparePlan,
  CompareRowConfig,
  CompareRowSource,
  CompareSectionConfig,
  ResolvedCompareRow,
  ResolvedCompareSection,
} from './types';

type OfferSource = Exclude<CompareRowSource, 'ATTRIBUTE'>;

type Copy = {
  included: string;
  notIncluded: string;
  /** Default labels of offer rows, used where the editor left the label empty. */
  offerLabels: Record<OfferSource, string>;
};

/**
 * The value one plan shows for one row, or `undefined` when the plan has nothing to show.
 * Numeric only for a single scalar `AttrNumber` that parses cleanly — a range, a compound text
 * (`S$200/day, max 20 days`) or a boolean is text and never takes part in highlighting.
 */
export function resolveCell(
  product: CmsProduct | undefined,
  attributeKey: string,
  copy: Copy,
): CompareCell | undefined {
  const attr = getAttrFromProductByName(attributeKey, product);
  if (!attr) return undefined;

  switch (attr.__typename) {
    case 'ComponentAttributesAttrNumber': {
      // Not a falsiness check: `0` is a real value (a zero excess wins a LOWER_BETTER row).
      const raw =
        attr.value === undefined || attr.value === null
          ? ''
          : String(attr.value).trim();
      // An empty number with authored copy ("Not covered") is something the editor chose to say,
      // so it renders instead of the page's generic dash — as the boolean branch already does.
      if (!raw && !attr.empty_value_text?.trim()) return undefined;
      const text = attributeCellText(attr);
      if (!text) return undefined;
      return { text, numeric: raw ? parsePriceValue(raw) : undefined };
    }
    case 'ComponentAttributesAttrBoolean': {
      if (attr.value === undefined || attr.value === null) return undefined;
      const authored = attr.value ? attr.true_value : attr.false_value;
      return {
        text:
          authored?.trim() || (attr.value ? copy.included : copy.notIncluded),
      };
    }
    default: {
      const text = attributeCellText(attr);
      return text ? { text } : undefined;
    }
  }
}

/**
 * The plan whose value wins, per the row's direction. Highlighting needs every compared plan to
 * hold a numeric value and a strict winner: a tie, a missing cell or a text cell means no mark.
 */
export function bestIndexFor(
  cells: (CompareCell | undefined)[],
  direction: CompareRowConfig['highlightDirection'],
): number | null {
  if (!direction || direction === 'NONE' || cells.length < 2) return null;
  if (cells.some((cell) => cell?.numeric === undefined)) return null;

  const values = cells.map((cell) => cell!.numeric!);
  const best =
    direction === 'HIGHER_BETTER' ? Math.max(...values) : Math.min(...values);
  const winners = values.filter((value) => value === best);
  if (winners.length !== 1) return null;
  return values.indexOf(best);
}

// The product's own label before its attribute type's: it is the one localized with the product,
// while a type's label can be another locale's (MoneyHero staging serves Chinese type labels on EN).
function labelFor(
  row: CompareRowConfig,
  attr: BaseAttribute | undefined,
): string {
  return (
    row.label?.trim() ||
    attr?.label?.trim() ||
    attr?.name?.label?.trim() ||
    row.attributeKey ||
    ''
  );
}

/**
 * The offers a product shows of one kind, as the results card shows them: exclusive offers include
 * the bundle ones, in that order. Only offers with a title a reader can read: `title` is the internal
 * campaign code. The mapper has already dropped offers outside their period.
 */
export function offersOf(
  product: CmsProduct | undefined,
  source: OfferSource,
): CmsOffer[] {
  return offersOfKind(product, source).filter(
    (offer) => !!offer.offer_title?.trim(),
  );
}

function offersOfKind(
  product: CmsProduct | undefined,
  source: OfferSource,
): CmsOffer[] {
  switch (source) {
    case 'EXCLUSIVE_OFFER':
      return product ? getChooseOneOffers(product) : [];
    case 'WELCOME_OFFER':
      return product?.welcome_offers ?? [];
    default:
      return source satisfies never;
  }
}

/**
 * A visible section's own fields, without its rows. Shared by the loaded and the loading page so the
 * two cannot drift apart.
 */
function sectionFields(
  section: CompareSectionConfig,
  highlight: boolean,
): Omit<ResolvedCompareSection, 'rows'> {
  return {
    key: section.key,
    label: section.label,
    bandLabel: section.bandLabel,
    note: section.note?.trim() || undefined,
    // The legend explains a mark, so it only appears where marking is on at all.
    showLegend: highlight && section.showLegend === true,
    defaultExpanded: section.defaultExpanded !== false,
  };
}

/**
 * Resolves the configured sections against the compared plans. A hidden row or section is left
 * out; a row no plan holds a value for is left out too, so the page never shows an empty line —
 * and a section left with no rows goes with them, rather than printing a bare heading over an
 * empty box.
 *
 * Marking the strongest value is the config's call, not a section's: a vertical whose attributes
 * share no unit turns it off everywhere rather than relying on every row being authored `NONE`.
 */
export function resolveSections(
  config: CompareConfig,
  plans: ComparePlan[],
  copy: Copy,
): ResolvedCompareSection[] {
  const highlight = config.highlightEnabled !== false;

  return config.sections
    .filter((section) => section.visible !== false)
    .map((section) => ({
      ...sectionFields(section, highlight),
      rows: resolveRows(section, plans, copy, highlight),
    }))
    .filter((section) => section.rows.length > 0);
}

/**
 * The sections as they stand before any plan has loaded: every visible row of the config, one line
 * each, with no values. What the page draws while it loads, so the table takes its height at once.
 * Which rows the plans actually hold cannot be known yet, so this is the full configured height —
 * the closest guess, not an exact one: `resolveSections` drops the rows no plan fills. A row with
 * no editor label has none yet (the attribute's own label comes with the plans).
 */
export function skeletonSections(
  config: CompareConfig,
  columns: number,
): ResolvedCompareSection[] {
  const highlight = config.highlightEnabled !== false;
  return config.sections
    .filter((section) => section.visible !== false)
    .map((section) => ({
      ...sectionFields(section, highlight),
      rows: section.rows
        .filter((row) => row.visible !== false)
        .map((row, index) => ({
          key: `${section.key}-${index}`,
          source: row.source ?? 'ATTRIBUTE',
          attributeKey: row.attributeKey ?? '',
          label: row.label?.trim() ?? '',
          cells: Array.from({ length: columns }, () => undefined),
          bestIndex: null,
        })),
    }))
    .filter((section) => section.rows.length > 0);
}

function resolveRows(
  section: CompareSectionConfig,
  plans: ComparePlan[],
  copy: Copy,
  highlight: boolean,
): ResolvedCompareRow[] {
  return section.rows
    .filter((row) => row.visible !== false)
    .flatMap((row, index): ResolvedCompareRow[] => {
      const source = row.source ?? 'ATTRIBUTE';
      if (source !== 'ATTRIBUTE') {
        return resolveOfferLines(row, source, index, plans, copy);
      }
      const attributeKey = row.attributeKey;
      if (!attributeKey) return [];
      const cells = plans.map((plan) =>
        resolveCell(plan.product, attributeKey, copy),
      );
      const firstAttr = plans
        .map((plan) => getAttrFromProductByName(attributeKey, plan.product))
        .find(Boolean);
      return [
        {
          // Index-suffixed: nothing stops an editor configuring one attribute twice in a section
          // (two labels, two directions), and React needs the rows to stay distinguishable.
          key: `${attributeKey}#${index}`,
          source,
          attributeKey,
          label: labelFor(row, firstAttr),
          cells,
          bestIndex: highlight
            ? bestIndexFor(cells, row.highlightDirection)
            : null,
        },
      ];
    })
    .filter((row) => row.cells.some(Boolean));
}

/** The design shows at most this many offers of one kind per plan. */
export const MAX_OFFER_LINES = 3;

/**
 * An offer row as lines: line N holds each plan's N-th offer of that kind, so a plan with two offers
 * fills two lines and one with a single offer leaves the second at the page's dash. At most
 * MAX_OFFER_LINES lines, the first offers in the card's order. The first line carries the row's
 * label; the rest carry none. An offer is text, so nothing is ever marked.
 */
function resolveOfferLines(
  row: CompareRowConfig,
  source: OfferSource,
  index: number,
  plans: ComparePlan[],
  copy: Copy,
): ResolvedCompareRow[] {
  const offersByPlan = plans.map((plan) => offersOf(plan.product, source));
  const lineCount = Math.min(
    MAX_OFFER_LINES,
    Math.max(0, ...offersByPlan.map((offers) => offers.length)),
  );
  const label = row.label?.trim() || copy.offerLabels[source];

  return Array.from({ length: lineCount }, (_, line) => ({
    key: `${source}#${index}.${line}`,
    source,
    attributeKey: '',
    label: line === 0 ? label : '',
    cells: offersByPlan.map((offers) => {
      const title = offers[line]?.offer_title?.trim();
      return title ? { text: title } : undefined;
    }),
    bestIndex: null,
  }));
}

/** The plan with the lowest offered premium, when every plan is priced and one is strictly lowest. */
export function bestPremiumIndex(plans: ComparePlan[]): number | null {
  const cells = plans.map((plan) => {
    const numeric = plan.premium?.amount;
    return numeric === undefined ? undefined : { text: '', numeric };
  });
  return bestIndexFor(cells, 'LOWER_BETTER');
}
